import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import styles from "./toolbox-label-style.module.css";
import { Toolbox } from "PosterEditor/sidebar/toolboxes/toolbox";
import {
  ToolboxComponentSection as Section,
  ToolboxSectionDivider as Divider,
} from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-section";
import { StaticLabel, TextAlignment } from "PosterEditor/model/poster-project";
import { ToolboxComponentColorPicker as ColorPicker } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-color-picker";
import { ToolboxComponentSlider as Slider } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-slider";
import { pickerColors } from "PosterEditor/model/poster-project";
import { defaultLabelStyle } from "PosterEditor/model/default-project-style";
import { useState, useEffect, useMemo } from "react";
import { DefaultStaticLabel } from "PosterEditor/model/default-project-style";
import { ToolboxComponentInput } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-input";
import { ToolboxComponentFontPicker as FontPicker } from "PosterEditor/sidebar/toolboxes/toolbox-components/toolbox-component-font-picker";
import {
  ToolboxComponentTextarea as TextArea,
  ToolboxComponentTextarea,
} from "../../toolbox-components/toolbox-component-textarea";
import { fontNames } from "PosterEditor/model/poster-project";
import {
  DeleteButton,
  VisibilityToggle,
  BackButton,
  UIIconButton,
} from "PosterEditor/common-components/ui-icon-drawing/ui-icon-button";
import {
  formatGPSData,
  GPSDataFormatted,
} from "PosterEditor/utils/gps-data-formatter";

interface ToolboxStaticLabelStyleProps {
  label: StaticLabel;
}

export const ToolboxStaticMapLabelStyle = (
  props: ToolboxStaticLabelStyleProps
) => {
  const {
    project,
    setStaticLabel: saveLabelToProject,
    getStaticLabel,
    removeStaticLabel,
    isTemplateEditing,
  } = usePosterProject();
  const { setEditingElement, editorUIState } = useEditorUI();

  const [actualFont, setActualFont] = useState<string | undefined>();
  const [previewingFont, setPreviewingFont] = useState<string | undefined>();

  const [label, setLabel] = useState<StaticLabel>(props.label);

  useEffect(() => {
    const labelID = props.label.id;
    const label = getStaticLabel(labelID) ?? DefaultStaticLabel;
    setActualFont(label.style.font);
    setLabel(label);
  }, [getStaticLabel, props.label?.id, props.label.position]);

  useEffect(() => {
    updateLabel(undefined, undefined, undefined);
  }, [previewingFont, actualFont]);

  useEffect(() => {
    setActualFont(label.style.font);
  }, []);

  const updateLabel = (
    text?: string,
    description?: string,
    style?: {
      backgroundColor?: string;
      backgroundOpacity?: number;
      borderRadius?: number;
      padding?: number;
      textColor?: string;
      fontSize?: number;
      fontName?: string;
      alignment?: TextAlignment;
      isUppercase?: boolean;
      isHidden?: boolean;
      lineSpacing?: number;
    }
  ) => {
    if (!label) {
      return;
    }
    const updatedLabel: StaticLabel = {
      ...label,
      position: label.position,
      text: text ?? label.text,
      description: description ?? label.description,
      style: {
        ...label.style,
        backgroundColor: style?.backgroundColor ?? label.style.backgroundColor,
        backgroundOpacity:
          style?.backgroundOpacity ?? label.style.backgroundOpacity,
        borderRadius: style?.borderRadius ?? label.style.borderRadius,
        padding: style?.padding ?? label.style.padding,
        textColor: style?.textColor ?? label.style.textColor,
        fontSize: style?.fontSize ?? label.style.fontSize,
        font: previewingFont ?? actualFont ?? label.style.font,
        alignment: style?.alignment ?? label.style.alignment,
        isUppercase: style?.isUppercase ?? label.style.isUppercase,
        isHidden: style?.isHidden ?? label.style.isHidden,
        lineSpacing: style?.lineSpacing ?? label.style.lineSpacing,
      },
    };
    setLabel(updatedLabel);
    saveLabelToProject(updatedLabel);
  };

  const mapWidth = editorUIState.mapSizeInPX?.width ?? 800;

  return (
    <div className={styles.container}>
      <Header
        label={label}
        onClickBackButton={() => setEditingElement(undefined)}
        onChangeLabelDescription={(text) => {
          updateLabel(undefined, text);
        }}
        onClickDelete={() => {
          removeStaticLabel(props.label);
          setEditingElement(undefined);
        }}
        onToggleVisibility={() => {
          updateLabel(undefined, undefined, {
            isHidden: !label.style.isHidden,
          });
        }}
      />
      <Toolbox collapsible={false} showTitleBar={false}>
        <div className={styles.paddedBodyContent}>
          {(isTemplateEditing ||
            project.editingPermissions.staticLabel.text) && (
            <>
              <TextContentControl
                text={label?.text ?? ""}
                onChangeText={updateLabel}
                isUppercase={label?.style.isUppercase ?? false}
                onSetUppercase={(isUppercase) => {
                  updateLabel(undefined, undefined, { isUppercase });
                }}
                onClickDeleteLabel={() => {
                  if (label) {
                    setEditingElement(undefined);
                    removeStaticLabel(label);
                  }
                }}
              />
              <Divider padding={0} />
              <GPSDataSection
                onClickSuggestion={(text) => {
                  if (label.style.isUppercase ?? false) {
                    updateLabel(text.toUpperCase());
                  } else {
                    updateLabel(text);
                  }
                }}
              />
            </>
          )}

          {(isTemplateEditing ||
            project.editingPermissions.staticLabel.font ||
            project.editingPermissions.staticLabel.color ||
            project.editingPermissions.staticLabel.alignment ||
            project.editingPermissions.staticLabel.size) && (
            <>
              <TextStyleControl
                font={actualFont ?? "Arial"}
                textColor={label?.style.textColor ?? "black"}
                lineSpacing={label?.style.lineSpacing ?? 100}
                onChangeLineSpacing={(lineSpacing) => {
                  updateLabel(undefined, undefined, { lineSpacing });
                }}
                fontSize={(label?.style.fontSize ?? 0.015) * mapWidth}
                onFontChange={(font) => {
                  setActualFont(font);
                  setPreviewingFont(undefined);
                }}
                onFontPreview={(fontName) => {
                  setPreviewingFont(fontName);
                }}
                onChangeTextColor={(color) =>
                  updateLabel(undefined, undefined, { textColor: color })
                }
                onChangeFontSize={(fontSize) =>
                  updateLabel(undefined, undefined, {
                    fontSize: fontSize / mapWidth,
                  })
                }
                alignment={label?.style.alignment ?? "center"}
                onChangeAlignment={(alignment) => {
                  updateLabel(undefined, undefined, { alignment });
                }}
              />
              <Divider />
            </>
          )}
          {(isTemplateEditing ||
            project.editingPermissions.staticLabel.backgroundColor ||
            project.editingPermissions.staticLabel.backgroundOpacity ||
            project.editingPermissions.staticLabel.backgroundPadding) && (
            <>
              <BackgroundControl
                backgroundColor={label?.style.backgroundColor ?? "white"}
                backgroundOpacity={
                  label?.style.backgroundOpacity ??
                  defaultLabelStyle.backgroundOpacity ??
                  0.5
                }
                padding={(label?.style.padding ?? 5) * mapWidth}
                onChangeBackgroundColor={(backgroundColor) => {
                  updateLabel(undefined, undefined, { backgroundColor });
                }}
                onChangeBackgroundOpacity={(backgroundOpacity) => {
                  updateLabel(undefined, undefined, { backgroundOpacity });
                }}
                onChangePadding={(padding) =>
                  updateLabel(undefined, undefined, {
                    padding: padding / mapWidth,
                  })
                }
              />
              <Divider />
            </>
          )}
        </div>
      </Toolbox>
    </div>
  );
};

interface HeaderProps {
  label: StaticLabel;
  onClickBackButton: () => void;
  onClickDelete: () => void;
  onToggleVisibility: () => void;
  onChangeLabelDescription: (text: string) => void;
}

const Header = (props: HeaderProps) => {
  const { isTemplateEditing } = usePosterProject();
  return (
    <div className={styles.header}>
      <div className={styles.headerLeading}>
        <BackButton onClick={props.onClickBackButton} />
        <ToolboxComponentInput
          variant="minimal"
          text={props.label.description}
          onChangeText={props.onChangeLabelDescription}
          disabled={!isTemplateEditing}
        />
      </div>
      <div className={styles.headerTrailing}>
        <VisibilityToggle
          isVisible={props.label.style.isHidden !== true}
          onToggle={props.onToggleVisibility}
        />
        {isTemplateEditing && <DeleteButton onClick={props.onClickDelete} />}
      </div>
    </div>
  );
};

interface BackgroundControlProps {
  backgroundColor: string;
  backgroundOpacity: number;
  padding: number;
  onChangeBackgroundColor: (color: string) => void;
  onChangeBackgroundOpacity: (opacity: number) => void;
  onChangePadding: (padding: number) => void;
}

const BackgroundControl = (props: BackgroundControlProps) => {
  const { project, isTemplateEditing } = usePosterProject();
  const [isColorExpanded, setIsColorExpanded] = useState(false);

  return (
    <Section title="Background">
      <div className={styles.toolboxRowContainer}>
        {(isTemplateEditing ||
          project.editingPermissions.staticLabel.backgroundColor) && (
          <ColorPicker
            showSwatch={true}
            colors={pickerColors}
            selectedColor={props.backgroundColor}
            onSelectColor={props.onChangeBackgroundColor}
            isExpanded={isColorExpanded}
            onToggleExpand={() => setIsColorExpanded((prev) => !prev)}
          />
        )}
        {(isTemplateEditing ||
          project.editingPermissions.staticLabel.backgroundOpacity) && (
          <Slider
            title="Opacity"
            min={0}
            max={100}
            value={props.backgroundOpacity * 100}
            onChange={(opacityPerc) => {
              const opacity = opacityPerc / 100;
              props.onChangeBackgroundOpacity(opacity);
            }}
          />
        )}
      </div>
      {(isTemplateEditing ||
        project.editingPermissions.staticLabel.backgroundPadding) && (
        <Slider
          title="Padding"
          min={0}
          max={20}
          value={props.padding}
          onChange={(value) => {
            props.onChangePadding(value);
          }}
        />
      )}
    </Section>
  );
};

interface TextStyleControlProps {
  font: string;
  textColor: string;
  fontSize: number;
  lineSpacing: number;
  onChangeLineSpacing: (lineSpacing: number) => void;
  onFontChange: (font: string) => void;
  onFontPreview: (font: string | undefined) => void;
  onChangeTextColor: (color: string) => void;
  onChangeFontSize: (fontSize: number) => void;
  alignment: TextAlignment;
  onChangeAlignment: (alignment: TextAlignment) => void;
}

const TextStyleControl = (props: TextStyleControlProps) => {
  const { project, isTemplateEditing } = usePosterProject();
  const [isColorExpanded, setIsColorExpanded] = useState(false);

  return (
    <Section title="Text style">
      {(isTemplateEditing || project.editingPermissions.staticLabel.font) && (
        <FontPicker
          fontNames={fontNames()}
          selectedFont={props.font ?? "Arial"}
          onSelectFont={(fontName) => {
            props.onFontChange(fontName);
          }}
          onPreviewFont={(fontName) => {
            props.onFontPreview(fontName);
          }}
          className={styles.fontPickerLabel}
        />
      )}

      <div className={styles.toolboxRowContainer}>
        {(isTemplateEditing ||
          project.editingPermissions.staticLabel.color) && (
          <ColorPicker
            showSwatch={true}
            colors={pickerColors}
            selectedColor={props.textColor}
            onSelectColor={props.onChangeTextColor}
            isExpanded={isColorExpanded}
            onToggleExpand={() => setIsColorExpanded((prev) => !prev)}
          />
        )}
        {(isTemplateEditing || project.editingPermissions.staticLabel.size) && (
          <Slider
            title={`Size (${props.fontSize}px)`}
            min={8}
            max={96}
            value={props.fontSize}
            onChange={(value) => {
              props.onChangeFontSize(value);
            }}
          />
        )}
      </div>
      {(isTemplateEditing ||
        project.editingPermissions.staticLabel.alignment) && (
        <div className={styles.toolboxRowContainer}>
          <UIIconButton
            width={20}
            height={20}
            iconName="TEXT-ALIGN-LEFT"
            isSelected={props.alignment === "left"}
            onClick={() => props.onChangeAlignment("left")}
          />
          <UIIconButton
            width={20}
            height={20}
            iconName="TEXT-ALIGN-CENTER"
            isSelected={props.alignment === "center"}
            onClick={() => props.onChangeAlignment("center")}
          />
          <UIIconButton
            width={20}
            height={20}
            iconName="TEXT-ALIGN-RIGHT"
            isSelected={props.alignment === "right"}
            onClick={() => props.onChangeAlignment("right")}
          />
          <Slider
            title="Line spacing"
            min={50}
            max={200}
            value={props.lineSpacing}
            onChange={(value) => {
              props.onChangeLineSpacing(value);
            }}
          />
        </div>
      )}
    </Section>
  );
};

interface TextContentControlProps {
  text: string;
  onChangeText: (text: string) => void;
  isUppercase: boolean;
  onSetUppercase: (isUppercase: boolean) => void;
  onClickDeleteLabel: () => void;
}

const TextContentControl = (props: TextContentControlProps) => {
  const onToggleUppercase = () => {
    props.onSetUppercase(!props.isUppercase);
  };

  useEffect(() => {
    if (props.isUppercase) {
      const text = props.text.toUpperCase();
      props.onChangeText(text);
    } else {
      const text = props.text.toLocaleLowerCase();
      props.onChangeText(text);
    }
  }, [props.isUppercase]);

  const onChangeText = (text: string) => {
    if (props.isUppercase) {
      text = text.toUpperCase();
    }
    props.onChangeText(text);
  };

  const text = props.isUppercase ? props.text.toUpperCase() : props.text;

  return (
    <Section>
      <div className={styles.toolboxRowContainer}>
        <ToolboxComponentTextarea
          text={text}
          onChangeText={onChangeText}
          autoFocusOnMount={true}
        />
        <UIIconButton
          iconName={props.isUppercase ? "TEXT-CASE-ON" : "TEXT-CASE-OFF"}
          padding={10}
          onClick={onToggleUppercase}
        />
      </div>
    </Section>
  );
};

interface GPXDataSectionProps {
  onClickSuggestion: (suggestion: string) => void;
}

const GPSDataSection = (props: GPXDataSectionProps) => {
  const { gpsSource } = usePosterProject();
  const [formattedData, setFormattedData] = useState<
    GPSDataFormatted | undefined
  >();

  useEffect(() => {
    if (gpsSource?.data) {
      let formatted = formatGPSData(gpsSource.data, "metric");
      setFormattedData(formatted);
    }
  }, [gpsSource]);

  if (!formattedData) {
    return null;
  }

  return (
    <>
      <Section title="Suggestions from GPS data">
        <div className={styles.gpxDataSuggestionsContainer}>
          <div className={styles.gpxDataSuggestions}>
            <GPSDataSuggestion
              title="Distance"
              text={formattedData.distance}
              onClick={props.onClickSuggestion}
            />
            <GPSDataSuggestion
              title="Time elapsed"
              text={formattedData.elapsed}
              onClick={props.onClickSuggestion}
            />
            <GPSDataSuggestion
              title="Start Date"
              text={formattedData.startDate}
              onClick={props.onClickSuggestion}
            />
            <GPSDataSuggestion
              title="End Date"
              text={formattedData.endDate}
              onClick={props.onClickSuggestion}
            />
          </div>
        </div>
      </Section>
      <Divider padding={0} />
    </>
  );
};

interface GPSDataSuggestionProps {
  title: string;
  text: string | undefined;
  onClick: (text: string) => void;
}

const GPSDataSuggestion = (props: GPSDataSuggestionProps) => {
  if (props.text !== undefined) {
    return (
      <div
        className={styles.gpxDataSuggestionButton}
        onClick={() => props.onClick(props.text!)}
      >
        <span className={styles.title}>{props.title}</span>
        <span className={styles.text}> {props.text}</span>
      </div>
    );
  }
  return null;
};
